// src/components/alertesCritiques/apiAlertesCritiques.js
// Appels du panneau des alertes critiques, de sa page de réglages et de sa
// page de suivi, journal des gestes sur le panneau, et clés de stockage du
// navigateur propres à chaque école, utilisateur et année.
//
// Le serveur calcule tout : quelles alertes, pour qui (licence puis
// permission), et l'action proposée sur chaque ligne. Le navigateur ne filtre
// rien, il affiche.

import { getAuthHeaders } from '../../services/api';

const NON_INSTALLE = "Les alertes critiques ne sont pas encore installées dans cet établissement.";

function erreurDuServeur(reponse, donnees, defaut) {
  let message = donnees?.message;
  if (!message && (reponse.status === 503 || (reponse.status === 404 && !donnees))) message = NON_INSTALLE;
  const erreur = new Error(message || donnees?.error || defaut);
  erreur.code = donnees?.code;
  erreur.statut = reponse.status;
  return erreur;
}

async function appeler(chemin, { methode = 'GET', corps } = {}) {
  let reponse;
  try {
    reponse = await fetch(chemin, {
      method: methode,
      headers: getAuthHeaders(),
      body: corps !== undefined ? JSON.stringify(corps) : undefined
    });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  let donnees = null;
  try { donnees = await reponse.json(); } catch { donnees = null; }
  if (!reponse.ok) throw erreurDuServeur(reponse, donnees, 'Une erreur est survenue.');
  return donnees;
}

/** Les alertes de l'utilisateur connecté, avec les réglages de rappel de l'école. */
export const lireAlertes = () => appeler('/api/alertes-critiques');

/** Constater une anomalie qu'on ne corrigera pas (validateurs seulement, motif obligatoire). */
export const constaterAlerte = ({ detecteur, entite_type, entite_id, empreinte, motif }) =>
  appeler('/api/alertes-critiques/constats', {
    methode: 'POST',
    corps: { detecteur, entite_type, entite_id, empreinte, motif }
  });

/** Réglages de l'école (Outils administratifs > Alertes critiques). */
export const lireReglagesAlertes = () => appeler('/api/parametres/alertes');

export const enregistrerReglagesAlertes = (reglages) =>
  appeler('/api/parametres/alertes', { methode: 'PUT', corps: reglages });

/**
 * Le suivi de l'administrateur (Outils administratifs > Alertes critiques >
 * Suivi) : alertes en cours, résolues, avancement et relances sur 30 jours.
 */
export const lireSuivi = () => appeler('/api/parametres/alertes/suivi');

/**
 * La page « Données suspectes » (Outils administratifs > Alertes critiques >
 * Données suspectes, 16/10/2026) : lecture pure, rien n'est écrit en
 * l'ouvrant. Le « Vérifié, c'est normal » passe par constaterAlerte.
 */
export const lireDonneesSuspectes = () => appeler('/api/parametres/alertes/donnees-suspectes');

// Les gestes que le navigateur journalise ; le constat, lui, l'est par le serveur.
const EVENEMENTS = new Set(['affichage', 'rappel', 'corriger']);

/**
 * Journalise un geste sur le panneau, pour la page de suivi :
 *   - 'affichage' : le panneau central s'est ouvert (le panneau latéral ne
 *                   l'est pas : version 1, sans migration) ;
 *   - 'rappel'    : « Me rappeler dans N min » ;
 *   - 'corriger'  : « Corriger », « Ouvrir la demande » ou « Voir la liste »,
 *                   avec la clé de la ligne, depuis l'un ou l'autre panneau
 *                   (une seule fois par ligne et par ouverture du panneau
 *                   latéral).
 * Ne lève jamais et ne s'attend pas : un journal perdu ne bloque rien et
 * n'affiche rien. `keepalive` : l'envoi survit à un F5 juste après le geste.
 */
export async function journaliserEvenement(type, { nb_a_traiter = null, cle = null } = {}) {
  if (!EVENEMENTS.has(type)) return;
  try {
    await fetch('/api/alertes-critiques/evenements', {
      method: 'POST',
      headers: getAuthHeaders(),
      keepalive: true,
      body: JSON.stringify({
        type,
        nb_a_traiter: Number.isInteger(nb_a_traiter) ? nb_a_traiter : null,
        cle: typeof cle === 'string' && cle ? cle : null
      })
    });
  } catch {
    // Silencieux : le journal ne doit jamais gêner l'agent.
  }
}

// --- Stockage du navigateur ------------------------------------------------
//
// Les clés des alertes critiques (suffixes de cleStockage) :
//   localStorage
//     alertesCritiques:echeance        échéance du panneau central (par année),
//                                      commune aux onglets
//     alertesCritiques:session         dernière session vue (par année)
//     alertesCritiques:intervalle      intervalle de l'école, en minutes
//     alertesCritiques:son             réglages de son de l'école (pour la cloche)
//     alertesCritiques:resume          résumé du bouton « Alertes » : détecteurs
//                                      ouverts et compte à traiter (par
//                                      utilisateur, toutes années)
//     alertesCritiques:progression     progression du jour du panneau latéral
//                                      (par année)
//     alertesCritiques:aideLaterale    « Comment ça marche » déjà déplié une fois
//     alertesCritiques:tailleLaterale  taille choisie du panneau latéral,
//                                      'standard' ou 'grande' (par
//                                      utilisateur, toutes années)
//   sessionStorage (par onglet)
//     alertesCritiques:panneauLateral  panneau latéral ouvert, avec la session
//                                      du jeton
// Les lectures et écritures passent par useAlertesCritiques.js (la taille du
// panneau latéral : par PanneauLateralAlertes.jsx), toujours protégées.

function utilisateurStocke() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

function anneeStockee() {
  try {
    return localStorage.getItem('annee_scolaire_id');
  } catch {
    return null;
  }
}

/**
 * Clé `klasys:<école>:<utilisateur>[:<année>]:<suffixe>`, sur le motif de
 * AdministrationLayout. Les notifications et les alertes sont liées à
 * l'année choisie à la connexion : `parAnnee` l'ajoute à la clé.
 * Ne lève jamais, même quand le stockage est interdit.
 */
export function cleStockage(suffixe, { parAnnee = true } = {}) {
  const user = utilisateurStocke();
  const ecole = user?.tenant_subdomain || window.location.hostname || 'default';
  const utilisateur = user?.id || user?.identifiant || 'anonyme';
  const annee = parAnnee ? `:${anneeStockee() || 'aucune'}` : '';
  return `klasys:${ecole}:${utilisateur}${annee}:${suffixe}`;
}

const SON_PAR_DEFAUT = { son_active: true, son_notifications: true };

/** Garde les réglages de son de l'école, pour que la cloche les lise sans appel. */
export function memoriserReglagesSon(reglages) {
  if (!reglages) return;
  try {
    localStorage.setItem(
      cleStockage('alertesCritiques:son', { parAnnee: false }),
      JSON.stringify({
        son_active: reglages.son_active !== false,
        son_notifications: reglages.son_notifications !== false
      })
    );
  } catch {
    // Stockage plein ou interdit : on garde les valeurs par défaut.
  }
}

export function reglagesSonMemorises() {
  try {
    const brut = localStorage.getItem(cleStockage('alertesCritiques:son', { parAnnee: false }));
    return brut ? { ...SON_PAR_DEFAUT, ...JSON.parse(brut) } : SON_PAR_DEFAUT;
  } catch {
    return SON_PAR_DEFAUT;
  }
}
